import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSectorInsightsSources } from './useSectorInsights';
import { formatMarketCapKrw, formatScore, formatSourceLabel } from './format';
import type { SectorInsightsSourceItem } from './types';

type SortKey =
  | 'source'
  | 'source_sector_name'
  | 'stock_count'
  | 'total_market_cap'
  | 'total_stock_score';
type SortDir = 'asc' | 'desc';

interface Props {
  canonicalKey: string;
}

export function SectorInsightsSourcesTab({ canonicalKey }: Props): JSX.Element {
  const { data, isLoading, isError, error } = useSectorInsightsSources(canonicalKey);
  // default: 스톡 스코어 합계 DESC.
  const [sortKey, setSortKey] = useState<SortKey>('total_stock_score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const items = data?.sources ?? [];

  const sorted = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') {
        cmp = av.localeCompare(bv, 'ko-KR');
      } else {
        cmp = (av as number) - (bv as number);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      const isNumeric =
        key === 'stock_count' || key === 'total_market_cap' || key === 'total_stock_score';
      setSortDir(isNumeric ? 'desc' : 'asc');
    }
  };

  return (
    <div className="pca-table-wrap">
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <table className="pca-table" data-testid="sector-insights-sources">
          <thead>
            <tr>
              <SortableTh label="원천 종류" k="source" cur={sortKey} dir={sortDir} onClick={handleSort} width={140} />
              <SortableTh label="원천 섹터명" k="source_sector_name" cur={sortKey} dir={sortDir} onClick={handleSort} />
              <SortableTh label="종목 수" k="stock_count" cur={sortKey} dir={sortDir} onClick={handleSort} width={100} align="right" />
              <SortableTh label="시가총액 (합계)" k="total_market_cap" cur={sortKey} dir={sortDir} onClick={handleSort} width={160} align="right" />
              <SortableTh label="스톡 스코어 (합계)" k="total_stock_score" cur={sortKey} dir={sortDir} onClick={handleSort} width={160} align="right" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="pca-empty">불러오는 중…</td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td colSpan={5} className="pca-empty">
                  원천 섹터 로드 실패: {(error as Error)?.message ?? 'unknown'}
                </td>
              </tr>
            )}
            {!isLoading && !isError && sorted.length === 0 && (
              <tr>
                <td colSpan={5} className="pca-empty">표시할 원천 섹터가 없습니다.</td>
              </tr>
            )}
            {sorted.map((it, i) => (
              <SourceRow key={`${it.source}-${it.source_sector_name}-${i}`} item={it} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SourceRow({ item }: { item: SectorInsightsSourceItem }): JSX.Element {
  return (
    <tr>
      <td>
        <span className="pca-status-badge">{formatSourceLabel(item.source)}</span>
      </td>
      <td>{item.source_sector_name}</td>
      <td style={{ textAlign: 'right' }}>{item.stock_count.toLocaleString('ko-KR')}</td>
      <td style={{ textAlign: 'right' }}>{formatMarketCapKrw(item.total_market_cap)}</td>
      <td style={{ textAlign: 'right' }}>{formatScore(item.total_stock_score)}</td>
    </tr>
  );
}

function SortableTh({
  label,
  k,
  cur,
  dir,
  onClick,
  width,
  align,
}: {
  label: string;
  k: SortKey;
  cur: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  width?: number;
  align?: 'left' | 'right';
}): JSX.Element {
  const active = k === cur;
  return (
    <th
      style={{
        width,
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align ?? 'left',
      }}
      onClick={() => onClick(k)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {active &&
          (dir === 'asc' ? (
            <ChevronUp size={12} strokeWidth={1.75} aria-hidden />
          ) : (
            <ChevronDown size={12} strokeWidth={1.75} aria-hidden />
          ))}
      </span>
    </th>
  );
}
